import React from 'react';

// Generic Loading Spinner
export const LoadingSpinner = ({ size = 'medium' }) => {
  const sizeMap = {
    small: 16,
    medium: 24,
    large: 32
  };

  const spinnerSize = sizeMap[size];

  return (
    <div 
      style={{
        width: spinnerSize,
        height: spinnerSize,
        border: '2px solid #f3f4f6',
        borderTop: '2px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}
    />
  );
};

// Loading skeleton for search results
export const SearchResultSkeleton = ({ count = 3 }) => (
  <div style={{ padding: '0 20px' }}>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} style={{
        padding: 16,
        borderBottom: '1px solid #e2e8f0',
        marginBottom: 8
      }}>
        {/* Name skeleton */}
        <div style={{
          height: 16,
          background: '#e5e7eb',
          borderRadius: 4,
          marginBottom: 8,
          width: `${60 + Math.random() * 30}%`,
          animation: 'shimmer 1.5s ease-in-out infinite'
        }} />
        
        {/* Description skeleton */}
        <div style={{
          height: 12,
          background: '#e5e7eb',
          borderRadius: 4,
          marginBottom: 4,
          width: '100%',
          animation: 'shimmer 1.5s ease-in-out infinite',
          animationDelay: '0.1s'
        }} />
        <div style={{
          height: 12,
          background: '#e5e7eb',
          borderRadius: 4,
          width: `${40 + Math.random() * 40}%`,
          animation: 'shimmer 1.5s ease-in-out infinite',
          animationDelay: '0.2s'
        }} />
        
        {/* State/location skeleton */}
        <div style={{
          height: 10,
          background: '#e5e7eb',
          borderRadius: 4,
          width: '30%',
          marginTop: 8,
          animation: 'shimmer 1.5s ease-in-out infinite',
          animationDelay: '0.3s'
        }} />
      </div>
    ))}
    
    <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes shimmer {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `
    }} />
  </div>
);

// Loading overlay for map
export const MapLoadingOverlay = () => (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(248, 250, 252, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    zIndex: 1000
  }}>
    <LoadingSpinner size="large" />
    <div style={{
      marginTop: 16,
      fontSize: 14,
      color: '#64748b',
      fontWeight: 500
    }}>
      Loading Map...
    </div>
  </div>
);

// Loading state for header search
export const SearchLoadingIndicator = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 8px',
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: 6,
    fontSize: 12,
    color: '#0284c7'
  }}>
    <LoadingSpinner size="small" />
    <span>Searching...</span>
  </div>
);

// Loading state for sidebar
export const SidebarLoadingState = () => (
  <div style={{
    width: 400,
    height: '100%',
    background: "white",
    borderRight: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    padding: 20
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      flexDirection: 'column'
    }}>
      <LoadingSpinner size="large" />
      <div style={{
        marginTop: 16,
        fontSize: 14,
        color: '#64748b',
        fontWeight: 500
      }}>
        Loading Facilities...
      </div>
    </div>
  </div>
);

// Empty state for no results
export const EmptySearchState = ({ searchTerm }) => (
  <div style={{
    padding: 40,
    textAlign: 'center',
    color: '#64748b'
  }}>
    <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 16 }}>
      No Results Found
    </div>
    <div style={{ fontSize: 14 }}>
      {searchTerm ? (
        <>No facilities found for "<strong>{searchTerm}</strong>". Try a different search term.</>
      ) : (
        'Enter a search term to find BLM facilities.'
      )}
    </div>
  </div>
);

// Error state for search
export const SearchErrorState = ({ error, onRetry }) => (
  <div style={{
    padding: 20,
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: 8,
    margin: 20,
    textAlign: 'center'
  }}>
    <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
    <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: 4 }}>
      Search Error
    </div>
    <div style={{ fontSize: 14, color: '#7f1d1d', marginBottom: 12 }}>
      {error || 'Unable to load search results.'}
    </div>
    {onRetry && (
      <button
        onClick={onRetry}
        style={{
          background: '#dc2626',
          color: 'white',
          border: 'none',
          borderRadius: 4,
          padding: '6px 12px',
          fontSize: 12,
          cursor: 'pointer'
        }}
      >
        Try Again
      </button>
    )}
  </div>
);

// Loading state for individual facility items
export const FacilityItemSkeleton = () => (
  <div style={{
    padding: 16,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    marginBottom: 8,
    background: 'white'
  }}>
    <div style={{
      height: 18,
      background: '#e5e7eb',
      borderRadius: 4,
      marginBottom: 8,
      width: '70%',
      animation: 'shimmer 1.5s ease-in-out infinite'
    }} />
    <div style={{
      height: 14,
      background: '#e5e7eb',
      borderRadius: 4,
      marginBottom: 4,
      width: '100%',
      animation: 'shimmer 1.5s ease-in-out infinite',
      animationDelay: '0.1s'
    }} />
    <div style={{
      height: 14,
      background: '#e5e7eb',
      borderRadius: 4,
      width: '60%',
      animation: 'shimmer 1.5s ease-in-out infinite',
      animationDelay: '0.2s'
    }} />
  </div>
);

export default {
  LoadingSpinner,
  SearchResultSkeleton,
  MapLoadingOverlay,
  SearchLoadingIndicator,
  SidebarLoadingState,
  EmptySearchState,
  SearchErrorState,
  FacilityItemSkeleton
};